import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2, MailWarning } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { trackEvent } from "@/lib/analytics";
import { usePersonalization } from "@/lib/personalization";
import { GUIDES, guideForProfile } from "@/lib/guides";
import GuideVisual from "@/components/sections/GuideVisual";
import { getAttribution } from "@/lib/attribution";
import { CommsConsent } from "@/components/CommsConsent";
import { buildCommercialConsent } from "@/lib/commercialConsent";
import { metaLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };

// Route-aware Free Guide. With a route → that route's guide. Without → visitor picks one of the three first.
export default function GuideMagnet() {
  const { profile, stage, need } = usePersonalization();
  const { section } = useSiteContent();
  const c = section("guide", HOME.guide);
  const routeGuide = guideForProfile(profile);
  const [picked, setPicked] = useState(null);
  const guide = routeGuide || picked;
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | sent | received | error
  const [fieldError, setFieldError] = useState("");
  const [phone, setPhone] = useState("");
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const started = useRef(false);
  const leadFired = useRef(false);

  useEffect(() => { if (routeGuide) setPicked(null); }, [routeGuide]);

  const onStart = () => {
    if (started.current) return;
    started.current = true;
    trackEvent("guide_form_start", { profile: profile || "neutral", guide_id: guide?.id });
  };

  const onPick = (g) => { setPicked(g); trackEvent("guide_selected", { guide_id: g.id, placement: "guide_section" }); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFieldError("");
    if (!guide) return setFieldError("Please choose a guide first.");
    if (!firstName.trim()) return setFieldError("Please tell us your first name.");
    if (!EMAIL_RE.test(email.trim())) return setFieldError("Please enter a valid email address.");
    setState("loading");
    trackEvent("guide_submit", { profile: profile || guide.profile, guide_id: guide.id });
    try {
      const r = await axios.post(`${API}/guide/request`, { first_name: firstName.trim(), email: email.trim(), guide_id: guide.id, profile: profile || guide.profile, stage, need, phone: phone.trim() || null, attribution: getAttribution(), commercial_consent: buildCommercialConsent({ email: emailOptIn, sms: smsOptIn }) });
      if (!leadFired.current) { leadFired.current = true; metaLead("guide"); } // backend accepted (2xx) → one Lead
      if (r.data?.email_sent) {
        setState("sent");
        trackEvent("guide_success", { guide_id: guide.id });
      } else {
        setState("received");
        trackEvent("guide_error", { status: "email_not_sent", guide_id: guide.id });
      }
    } catch (err) {
      setState("error");
      trackEvent("guide_error", { status: err?.response?.status || 0, guide_id: guide.id });
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/30 focus:border-[#B3A17D] text-white placeholder:text-white/40 py-3 focus:outline-none";
  const labelCls = "block text-[10px] tracking-[0.24em] uppercase text-white/55 mb-1";

  return (
    <section id="guide" tabIndex={-1} data-testid="guide-section" data-guide={guide?.id || "none"} className="bg-[#451C25] text-white py-24 md:py-32 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <p className="text-[#B3A17D] text-[11px] tracking-[0.3em] uppercase mb-6 font-semibold" style={K}>{c.eyebrow}</p>
          <h2 data-testid="guide-headline" className="text-4xl md:text-5xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.headline}</h2>
          <p className="mt-7 max-w-xl text-white/80 text-base md:text-lg leading-relaxed" style={S}>{guide ? guide.blurb : c.text}</p>
          <p className="mt-3 text-white/60 text-[15px]" style={S}>{c.text2}</p>

          {state === "sent" && (
            <div data-testid="guide-success" className="mt-10 max-w-xl border border-[#B3A17D]/50 p-6 flex items-start gap-4">
              <CheckCircle2 size={22} className="text-[#B3A17D] shrink-0 mt-0.5" />
              <div>
                <p className="text-xl" style={{ ...K, fontWeight: 500 }}>Check your inbox.</p>
                <p className="mt-1 text-white/75 text-[15px] leading-relaxed">We've sent the <span className="text-white">{guide.title}</span> from contact@destinoeurope.com to <span className="text-white">{email.trim()}</span>. If it isn't there in a couple of minutes, check your spam folder.</p>
              </div>
            </div>
          )}
          {state === "received" && (
            <div data-testid="guide-received" role="status" className="mt-10 max-w-xl border border-[#E8C9A0]/60 p-6 flex items-start gap-4">
              <MailWarning size={22} className="text-[#E8C9A0] shrink-0 mt-0.5" />
              <div>
                <p className="text-xl" style={{ ...K, fontWeight: 500 }}>Request received: email not sent yet.</p>
                <p className="mt-1 text-white/75 text-[15px] leading-relaxed">We've saved your request for the <span className="text-white">{guide.title}</span>, but the email to <span className="text-white">{email.trim()}</span> could not be sent right now. We'll follow up from <span className="text-white">contact@destinoeurope.com</span> as soon as possible.</p>
              </div>
            </div>
          )}

          {state !== "sent" && state !== "received" && (
            <>
              {!guide && (
                <fieldset data-testid="guide-picker" className="mt-10 max-w-xl">
                  <legend className="text-lg" style={{ ...K, fontWeight: 500 }}>Which guide would you like?</legend>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {GUIDES.map((g) => (
                      <button key={g.id} type="button" data-testid={`guide-pick-${g.id}`} onClick={() => onPick(g)} className="group text-left border border-white/25 hover:border-[#B3A17D] px-4 py-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]">
                        <span className="block text-[10px] tracking-[0.24em] uppercase text-[#B3A17D]" style={K}>{g.route_label}</span>
                        <span className="mt-1.5 flex items-center justify-between gap-2 text-[15px] text-white" style={{ ...K, fontWeight: 500 }}>{g.title}<ArrowRight size={14} className="shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></span>
                      </button>
                    ))}
                  </div>
                  {fieldError && <p data-testid="guide-field-error" role="alert" className="mt-3 text-[#E8C9A0] text-[13px]">{fieldError}</p>}
                </fieldset>
              )}

              {guide && (
                <form data-testid="guide-form" onSubmit={onSubmit} noValidate className="mt-10 max-w-xl grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <p data-testid="guide-selected" className="sm:col-span-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[15px]" style={S}>
                    <span className="text-white/60">Your guide:</span>
                    <span className="text-white" style={{ ...K, fontWeight: 500 }}>{guide.title}</span>
                    {!routeGuide && <button type="button" data-testid="guide-change" onClick={() => setPicked(null)} className="text-[12px] text-white/70 underline underline-offset-4 decoration-[#B3A17D] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]" style={K}>Change</button>}
                  </p>
                  <label className="block">
                    <span className={labelCls}>{c.first_name_label}</span>
                    <input data-testid="guide-first-name" value={firstName} onFocus={onStart} onChange={(e) => setFirstName(e.target.value)} className={inputCls} placeholder="Linda" autoComplete="given-name" />
                  </label>
                  <label className="block">
                    <span className={labelCls}>{c.email_label}</span>
                    <input data-testid="guide-email" type="email" value={email} onFocus={onStart} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="linda@example.com" autoComplete="email" />
                  </label>
                  {fieldError && <p data-testid="guide-field-error" role="alert" className="sm:col-span-2 text-[#E8C9A0] text-[13px]">{fieldError}</p>}
                  {state === "error" && !fieldError && (
                    <p data-testid="guide-server-error" role="alert" className="sm:col-span-2 text-[#E8C9A0] text-[13px]">Something went wrong on our side and the guide wasn't sent. Please try again in a moment.</p>
                  )}
                  <label className="block sm:col-span-2">
                    <span className={labelCls}>Mobile phone (optional)</span>
                    <input data-testid="guide-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="+1 305 555 0142" autoComplete="tel" />
                  </label>
                  <div className="sm:col-span-2 mt-1">
                    <CommsConsent variant="onDark" showEmail showSms email={emailOptIn} sms={smsOptIn} onEmailChange={setEmailOptIn} onSmsChange={setSmsOptIn} />
                  </div>
                  <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 mt-2">
                    <button data-testid="guide-submit" type="submit" disabled={state === "loading"} className="group inline-flex items-center justify-center gap-3 bg-[#B3A17D] text-[#2B1017] px-8 py-4 text-[12px] tracking-[0.2em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-70 disabled:cursor-wait" style={K}>
                      {state === "loading" ? (<><Loader2 size={15} className="animate-spin" /> Sending…</>) : (<>{guide.cta} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></>)}
                    </button>
                    <span data-testid="guide-note" className="text-white/50 text-[12px]">{c.note}</span>
                  </div>
                </form>
              )}
            </>
          )}
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end lg:pr-4">
          <GuideVisual guide={guide} />
        </div>
      </div>
    </section>
  );
}
